import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import type { LocalOverrides } from '@atlaskit/feature-gate-js-client/persistent-override-adapter';

import { confluenceLocalStorageInstance } from '@confluence/storage-manager/entry-points/storageInstance.ts';
import { PERSISTED_KEYS_ON_SERVER } from '@confluence/storage-manager/entry-points/LocalStorageKeys.ts';
import {
	processQueryParamFeatureGates,
	processQueryParamExperiments,
} from '@confluence/feature-flag-url-override';
import { getSSRRenderInputs } from '@confluence/ssr-utilities/entry-points/ssrRenderInputs.ts';
import { getLogger } from '@confluence/logger';
import { StatsigConfigurations } from '@confluence/statsig-configurations/entry-points/StatsigConfigurations.ts';

const logger = getLogger('persistedStatsigOverrides');

type PersistedStatsigOverrides = Partial<LocalOverrides> & {
	disabledOverrides?: LocalOverrides;
};

const EMPTY_OVERRIDES: LocalOverrides = {
	configs: {},
	gates: {},
	layers: {},
};

const getPersistedStatsigOverrides = (): PersistedStatsigOverrides | null => {
	if (!confluenceLocalStorageInstance) {
		return null;
	}

	return confluenceLocalStorageInstance.getItem(
		PERSISTED_KEYS_ON_SERVER.PERSISTED_OVERRIDES_STATSIG,
	);
};

export const updatePersistedStatsigOverrides = (overrides: PersistedStatsigOverrides): void => {
	if (confluenceLocalStorageInstance) {
		confluenceLocalStorageInstance.setItem(
			PERSISTED_KEYS_ON_SERVER.PERSISTED_OVERRIDES_STATSIG,
			overrides,
		);
	}
};

export const getDisabledPersistedStatsigOverrides = (): LocalOverrides | null =>
	getPersistedStatsigOverrides()?.disabledOverrides ?? null;

export const setPersistedStatsigOverridesDisabled = (isDisabled: boolean): void => {
	if (isDisabled) {
		const disabledOverrides = getDisabledPersistedStatsigOverrides() ?? FeatureGates.getOverrides();
		updatePersistedStatsigOverrides({
			...EMPTY_OVERRIDES,
			disabledOverrides,
		});
		FeatureGates.clearAllOverrides();
		return;
	}

	const disabledOverrides = getDisabledPersistedStatsigOverrides();
	if (disabledOverrides) {
		updatePersistedStatsigOverrides(disabledOverrides);
		FeatureGates.setOverrides(disabledOverrides);
		return;
	}

	logger.warn`setPersistedStatsigOverridesDisabled(false): no disabledOverrides found in storage, skipping restore`;
};

export const applyPersistedStatsigOverrides = (
	additionalOverrides?: Partial<LocalOverrides>,
): void => {
	let overrides: PersistedStatsigOverrides | undefined;

	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	if (process.env.REACT_SSR) {
		// On server overrides are provided by cc-frontend-ssr or bifrost
		overrides = getSSRRenderInputs().persistedStatsigOverrides;
	} else if (window.__SSR_RENDERED__) {
		// When hydrate overrides are provided by server
		overrides = window?.__STATSIG_BOOTSTRAP__?.persistedStatsigOverrides;
	} else {
		// Otherwise get from persisted storage
		overrides = getPersistedStatsigOverrides() ?? undefined;
	}

	let configs = overrides?.configs || {};
	let gates = overrides?.gates || {};
	let layers = overrides?.layers || {};

	// On client-side PlayWright have client only overrides
	try {
		const rawClientOverrides = window.sessionStorage.getItem('clientOnlyOverrides');
		const clientOnlyOverrides = JSON.parse(rawClientOverrides || '{}');
		if (clientOnlyOverrides) {
			configs = { ...configs, ...clientOnlyOverrides?.configs };
			gates = { ...gates, ...clientOnlyOverrides?.gates };
			layers = { ...layers, ...clientOnlyOverrides?.layers };
		}
	} catch {}

	// Finally user can override with ?fg=fg1:value,fg2:value
	// Server will handle fg query and pass along in persistedStatsigOverrides
	// Here we are only handling no-SSR case
	const urlOverrideGates = processQueryParamFeatureGates();
	if (urlOverrideGates) {
		gates = { ...gates, ...urlOverrideGates };
	}

	// User can override experiments with ?exp=experimentName:value (defaults to isEnabled property)
	// or ?exp=experimentName:property:value
	const urlOverrideExperiments = processQueryParamExperiments();
	if (urlOverrideExperiments) {
		configs = { ...configs, ...urlOverrideExperiments };
	}

	if (additionalOverrides) {
		gates = { ...gates, ...additionalOverrides?.gates };
		configs = { ...configs, ...additionalOverrides?.configs };
		layers = { ...layers, ...additionalOverrides?.layers };
	}

	FeatureGates.setOverrides({ configs, layers, gates });
	logger.info`Statsig overrides applied: ${{ configs, layers, gates }}`;

	// Re-initialize StatsigConfigurations to pick up the new overrides
	// This is necessary because StatsigConfigurations may have been initialized
	// before FeatureGates overrides were set
	StatsigConfigurations.initializeOverrides();
};
